import { useSubscription, useLazyQuery } from '@apollo/client/react';
import { css, Theme } from '@emotion/react';
import { NoSsr, Skeleton, Typography } from '@mui/material';
import { Box, BoxProps } from '@mui/system';
import {
  Types,
  useTranslation,
  EventListenInfosDocument,
} from '@scorenco/core';

import { ClubLogo, Score } from '../../components';
import { T_KEYS } from '../../translations';

import { RefetchEventDocument } from './EventItem.query.generated';

const toDateInput = (v: unknown): string | number | Date =>
  typeof v === 'string' || typeof v === 'number' || v instanceof Date ? v : '';

type PlayingTeam = {
  score?: unknown;
  data?: { shootout?: unknown; sets?: unknown[] };
  logo?: string;
  name?: string;
  name_in_competition?: string;
  name_in_club?: string;
  club_id?: number;
  is_team_of_club?: number;
};

const style = {
  root: (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem 0 1rem;
    width: 100%;
    margin: 0 auto;

    .event-content {
      display: flex;
      align-items: center;
      justify-content: space-around;
      padding: 0.5rem 0;
    }

    .team-logo {
      width: 1.25rem;
      height: 2rem;
      position: relative;
    }

    .team-name {
      flex: 1;
      padding: 0 1rem;
      font-size: 0.8rem;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .team-name-away {
      text-align: right;
    }

    .score-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .scores {
      display: flex;
      justify-content: center;
      margin: 0.25rem 0;
      font-weight: bold;
    }

    .score-withdrawal {
      text-align: center;
      font-size: 10px;
      font-style: italic;
    }

    .give-score {
      text-align: center;
      font-size: 10px;
      font-style: italic;
    }

    .score-shootout {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${theme.vars.palette.background.default};
      margin: 0 0.75rem;
      padding: 0 1.5rem;
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      opacity: 0.75;
    }

    .footer {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f5f5f5;
      margin: 0 1rem;
      padding: 0 1.5rem;
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      opacity: 0.75;
    }

    .footer-text {
      font-size: 0.75rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .footer-live {
      position: relative;
      background-color: #cc062a15;

      &::before {
        position: absolute;
        left: 0.5rem;
        content: '';
        height: 0.5rem;
        width: 0.5rem;
        margin-right: 0.25rem;
        background-color: #cc062a;
        border-radius: 9999px;
        display: inline-block;
      }
    }

    .date {
      text-align: center;
      font-size: 0.8em;
      line-height: 1.2;
      font-style: italic;

      > *:last-of-type {
        font-size: 1em;
        line-height: 1.2;
        font-style: normal;
        font-weight: 500;
      }
    }

    .bold {
      font-weight: 700;
    }
  `,
};

type TeamNameProps = {
  className?: string;
  team?: any;
  clubId?: number;
  showShortName?: boolean;
};

const TeamName = ({
  className,
  team,
  clubId,
  showShortName,
}: TeamNameProps) => {
  return (
    <div
      className={[
        'event-item_team',
        className,
        clubId &&
          (team?.club_id === clubId || team?.is_team_of_club === 1) &&
          'bold',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {((team?.club_id === clubId || team?.is_team_of_club === 1) &&
      showShortName === true
        ? team?.name_in_club
        : team?.name_in_competition) || <Skeleton />}
    </div>
  );
};

export type EventItemProps = BoxProps & {
  event?: Types.Competitions_Event_Detail;
  condensed?: boolean;
  clubId?: number;
  showShortName?: boolean;
  showCompetition?: boolean;
  showPlace?: boolean;
  forcedPlace?: string;
  showDate?: boolean;
  scoreSubscription?: boolean;
  isTournament?: boolean;
};

export const EventItem = ({
  event,
  showCompetition = true,
  condensed = false,
  clubId,
  showShortName = false,
  showPlace = true,
  forcedPlace = '',
  showDate = false,
  scoreSubscription = false,
  isTournament = false,
  ...rest
}: EventItemProps) => {
  const { t } = useTranslation();

  const home_score = (event?.teams as PlayingTeam[] | undefined)?.[0]?.score as
    | number
    | undefined;
  const home_shootout_score = (event?.teams as PlayingTeam[] | undefined)?.[0]
    ?.data?.shootout as number | undefined;
  const home_sets_score = (event?.teams as PlayingTeam[] | undefined)?.[0]?.data
    ?.sets as number[] | undefined;
  const away_score = (event?.teams as PlayingTeam[] | undefined)?.[1]?.score as
    | number
    | undefined;
  const away_shootout_score = (event?.teams as PlayingTeam[] | undefined)?.[1]
    ?.data?.shootout as number | undefined;
  const away_set_score = (event?.teams as PlayingTeam[] | undefined)?.[1]?.data
    ?.sets as number[] | undefined;

  const has_shootout =
    (home_shootout_score ?? 0) > 0 || (away_shootout_score ?? 0) > 0;
  const has_sets =
    (home_sets_score?.length ?? 0) > 0 && (away_set_score?.length ?? 0) > 0;

  const [refetchEvent] = useLazyQuery(RefetchEventDocument, {
    fetchPolicy: 'network-only',
  });

  useSubscription(EventListenInfosDocument, {
    variables: { eventId: Number(event?.id) },
    context: {
      role: 'anonymous',
    },
    skip:
      !event?.id ||
      !scoreSubscription ||
      // Si c'est un tournoi, on ne veut que les mises à jour 2 heures avant et 2 heures après
      (isTournament
        ? new Date(toDateInput(event?.time)).getTime() <
            new Date().getTime() - 7200000 ||
          new Date(toDateInput(event?.time)).getTime() >
            new Date().getTime() + 7200000
        : (typeof event?.date === 'string' ? event.date : '').split('T')[0] !==
          new Date().toISOString().split('T')[0]),
    onData: () => {
      refetchEvent({
        variables: { eventId: Number(event?.id) },
        context: { role: 'anonymous' },
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const isStarted =
    (Array.isArray(event?.teams) ? (event.teams as PlayingTeam[]) : []).reduce(
      (acc: boolean, team: PlayingTeam) =>
        acc && team?.score !== null && team?.score !== undefined,
      true
    ) ?? false;

  const isLive = event?.status === 'live';

  let scoreboard;
  if (event?.status === 'to_come') {
    scoreboard = (
      <div className="date">
        {showDate && !!event?.date && (
          <div>
            {new Date(
              toDateInput(event.time) || toDateInput(event.date)
            ).toLocaleDateString('fr-FR', {
              month: 'short',
              day: '2-digit',
            })}
          </div>
        )}
        {!!event?.time && (
          <div>
            {new Date(toDateInput(event.time)).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        )}
      </div>
    );
  } else if (event?.status === 'postponed') {
    scoreboard = <div className="give-score">{t(T_KEYS.EVENT_POSTPONED)}</div>;
  } else if (event?.status === 'live' && !isStarted) {
    scoreboard = (
      <div
        className="give-score"
        dangerouslySetInnerHTML={{
          __html: t(T_KEYS.EVENT_CARD_GIVE_SCORE) as string,
        }}
      />
    );
  } else {
    scoreboard = (
      <div className="scores-container">
        {event?.status === 'withdrawal' && (
          <div className="score-withdrawal">{t(T_KEYS.EVENT_WITHDRAWAL)}</div>
        )}
        <div className="scores">
          <Score
            score={home_score ?? 0}
            opponentScore={away_score ?? 0}
            isWinner={
              (event?.status === 'finish' || event?.status === 'withdrawal') &&
              ((home_score ?? 0) > (away_score ?? 0) ||
                ((home_score ?? 0) === (away_score ?? 0) &&
                  (home_shootout_score ?? 0) > (away_shootout_score ?? 0)))
            }
          />
          <Score
            score={away_score ?? 0}
            opponentScore={home_score ?? 0}
            isWinner={
              (event?.status === 'finish' || event?.status === 'withdrawal') &&
              ((home_score ?? 0) < (away_score ?? 0) ||
                ((home_score ?? 0) === (away_score ?? 0) &&
                  (home_shootout_score ?? 0) < (away_shootout_score ?? 0)))
            }
          />
        </div>
        {has_sets && (
          <Typography variant="body2" fontSize={10} className="score-shootout">
            SETS :{' '}
            {home_sets_score
              ?.map(
                (homeScore: number, index: number) =>
                  `${homeScore}:${away_set_score?.[index] || 0}`
              )
              .join(', ')}
          </Typography>
        )}
        {has_shootout && (
          <Typography variant="body2" fontSize={10} className="score-shootout">
            TAB : {home_shootout_score} - {away_shootout_score}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <Box css={style.root} {...rest}>
      <div className="event-content">
        <ClubLogo
          className="team-logo"
          logo={(event?.teams as PlayingTeam[] | undefined)?.[0]?.logo}
          name={
            (event?.teams as PlayingTeam[] | undefined)?.[0]
              ?.name_in_competition
          }
          shieldColor="home"
          width={64}
          height={64}
        />

        {!condensed && (
          <TeamName
            className="team-name"
            team={(event?.teams as PlayingTeam[] | undefined)?.[0]}
            clubId={clubId}
            showShortName={showShortName}
          />
        )}

        <NoSsr>{scoreboard}</NoSsr>

        {!condensed && (
          <TeamName
            className="team-name team-name-away"
            team={(event?.teams as PlayingTeam[] | undefined)?.[1]}
            clubId={clubId}
            showShortName={showShortName}
          />
        )}

        <ClubLogo
          className="team-logo"
          logo={(event?.teams as PlayingTeam[] | undefined)?.[1]?.logo}
          name={(event?.teams as PlayingTeam[] | undefined)?.[1]?.name}
          shieldColor="away"
          width={64}
          height={64}
        />
      </div>

      {isTournament ? (
        <div
          className={['footer', isLive && 'footer-live']
            .filter(Boolean)
            .join(' ')}
        >
          <div className="footer-text">
            {[isLive && t(T_KEYS.EVENT_LIVE)].filter(Boolean).join(' - ')}
          </div>
        </div>
      ) : showCompetition && !condensed ? (
        <div
          className={['footer', isLive && 'footer-live']
            .filter(Boolean)
            .join(' ')}
        >
          <div className="footer-text">
            {[
              isLive && !event?.level_name
                ? t(T_KEYS.EVENT_LIVE)
                : event?.level_name,

              showPlace && (forcedPlace || event?.place?.name),
            ]
              .filter(Boolean)
              .join(' - ')}
          </div>
        </div>
      ) : (
        <div
          className={['footer', isLive && 'footer-live']
            .filter(Boolean)
            .join(' ')}
        >
          <div className="footer-text">
            {[isLive && t(T_KEYS.EVENT_LIVE)].filter(Boolean).join(' - ')}
          </div>
        </div>
      )}
    </Box>
  );
};
